import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { materialClient } from 'api';
import type {
  Material,
  MaterialAtStoreToAdd,
  MaterialAtStoreToMove,
  MaterialPriceOperation,
  MaterialToEdit
} from 'api/models/mainservice';
import type { StoreDetailV2, StoreToAddV2, StoreToEditV2, StoreV2 } from 'api/models/unified';
import type { ApiError, TableResult } from 'common/models';
import { getInfiniteNextPageParam } from 'helpers/queriesHelper';
import { defaultMaterialTableQuery, getTableQueryKey } from 'helpers/tableHelpers';
import { useNavigate } from 'react-router-dom';

import type {
  ExistingMaterial,
  ExistingMaterialQuery,
  MaterialTableQuery,
  MaterialToAdd,
  Store,
  StoreDetail,
  StoreMaterialTableQuery
} from './models';
import { defaultStoreMaterialTableQuery } from './store/StoreDetailContent';

const storesKey = 'stores';
const storeDetailKey = 'storeDetail';
const materialKey = 'material';
const existingMaterialKey = 'existingMaterial';
const materialDetailKey = 'materialDetail';

export const getStoresQueryKeyBuilder = () => [storesKey];
export const getStoreDetailQueryKeyBuilder = (id: string) => [storeDetailKey, id];
export const getMaterialQueryKeyBuilder = () => [materialKey];
export const getExistingMaterialKeyBuilder = (params: ExistingMaterialQuery) => [
  [existingMaterialKey],
  params.identificationNumber || ''
];

export const getMaterialDetailQueryKeyBuilder = (id: string) => [materialDetailKey, id];

export const useGetStoresQuery = () => {
  return useQuery<Store[], ApiError>(getStoresQueryKeyBuilder(), () => materialClient.getStores());
};

export const useGetStoresV2Query = () => {
  return useQuery<StoreV2[], ApiError>(getStoresQueryKeyBuilder(), () =>
    materialClient.getStoresV2()
  );
};

export const useGetStoreDetailQuery = (id: string, params: StoreMaterialTableQuery) => {
  return useQuery<StoreDetailV2, ApiError>(
    getTableQueryKey(storeDetailKey, params, id),
    () => materialClient.getStoreDetailv2(id, params),
    {
      enabled: !!id
    }
  );
};

export const useGetStoreDetailInfiniteQuery = (
  storeId: string,
  params: StoreMaterialTableQuery
) => {
  return useInfiniteQuery<StoreDetail, ApiError>(
    getTableQueryKey(storeDetailKey, params, storeId, true),
    ({ pageParam = 1 }) =>
      materialClient.getStoreDetail(storeId, {
        ...params,
        page: pageParam
      }),
    {
      staleTime: 0,
      keepPreviousData: true,
      getNextPageParam: (lastPage, pages) =>
        getInfiniteNextPageParam(
          lastPage.material.data,
          pages.map((page) => page.material.data),
          defaultStoreMaterialTableQuery.pageSize
        )
    }
  );
};

export const useGetMaterialQuery = (params: MaterialTableQuery) => {
  return useQuery<TableResult<Material>, ApiError>(getTableQueryKey(materialKey, params), () =>
    materialClient.getMaterial(params)
  );
};

export const useGetMaterialInfiniteQuery = (params: MaterialTableQuery) => {
  return useInfiniteQuery<TableResult<Material>, ApiError>(
    getTableQueryKey(materialKey, params, undefined, true),
    ({ pageParam = 1 }) =>
      materialClient.getMaterial({
        ...params,
        page: pageParam
      }),
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage, pages) => {
        const nextPageParam = getInfiniteNextPageParam(
          lastPage.data,
          pages.map((page) => page.data),
          defaultMaterialTableQuery.pageSize
        );

        return nextPageParam;
      }
    }
  );
};

export const useAddStoreV2Mutation = () => {
  const queryClient = useQueryClient();

  return useMutation<StoreV2, ApiError, StoreToAddV2>(materialClient.addStoreV2, {
    onSuccess: (data) => {
      queryClient.setQueryData<StoreV2[] | undefined>(
        getStoresQueryKeyBuilder(),
        (oldData) => oldData && [...oldData, data]
      );
    }
  });
};

export const useEditStoreV2Mutation = () => {
  const queryClient = useQueryClient();

  return useMutation<StoreV2, ApiError, { storeId: string; values: StoreToEditV2 }>(
    materialClient.editStoreV2,
    {
      onSuccess: async (data, { storeId }) => {
        await queryClient.invalidateQueries(getStoresQueryKeyBuilder());
        await queryClient.invalidateQueries(getStoreDetailQueryKeyBuilder(storeId));
      }
    }
  );
};

export const useDeleteStoreMutation = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<undefined, ApiError, string>(materialClient.deleteStore, {
    onSuccess: async (data, storeId) => {
      navigate('/material');
      await queryClient.invalidateQueries(getStoreDetailQueryKeyBuilder(storeId));
      await queryClient.invalidateQueries(getMaterialQueryKeyBuilder());
      queryClient.setQueryData<Store[] | undefined>(getStoresQueryKeyBuilder(), (oldData) =>
        oldData?.filter((x) => x.id !== storeId)
      );
    }
  });
};

export const useAddMaterialMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Material, ApiError, MaterialToAdd>(materialClient.addMaterial, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(getMaterialQueryKeyBuilder());
    }
  });
};

export const useEditMaterialMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { materialId: string; data: MaterialToEdit }>(
    (params) => materialClient.editMaterial(params),
    {
      onSuccess: async (data, { materialId }) => {
        await queryClient.invalidateQueries(getMaterialDetailQueryKeyBuilder(materialId));
        await queryClient.invalidateQueries(getMaterialQueryKeyBuilder());
      }
    }
  );
};

export const useDeleteMaterialMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(materialClient.deleteMaterial, {
    onSuccess: async () => {
      await queryClient.removeQueries(getMaterialQueryKeyBuilder());
      await queryClient.removeQueries([storeDetailKey]);
    }
  });
};

export const useAddMaterialToStoreMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { data: MaterialAtStoreToAdd[]; storeId: string }>(
    materialClient.addMaterialToStore,
    {
      onSuccess: async (data, { storeId }) => {
        await queryClient.removeQueries(getStoreDetailQueryKeyBuilder(storeId));
        await queryClient.invalidateQueries(getMaterialQueryKeyBuilder());
      }
    }
  );
};

export const useMoveMaterialToStoreMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      data: MaterialAtStoreToMove[];
      storeId: string;
      moveToId: string;
    }
  >(materialClient.moveToStore, {
    onSuccess: async (data, { storeId, moveToId }) => {
      await queryClient.removeQueries(getStoreDetailQueryKeyBuilder(storeId));
      await queryClient.removeQueries(getStoreDetailQueryKeyBuilder(moveToId));
      await queryClient.invalidateQueries(getMaterialQueryKeyBuilder());
    }
  });
};

export const useRemoveMaterialFromStoreMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { storeId: string; data: MaterialAtStoreToMove[] }>(
    materialClient.removeFromStore,
    {
      onSuccess: async (data, { storeId }) => {
        await queryClient.removeQueries(getStoreDetailQueryKeyBuilder(storeId));
        await queryClient.invalidateQueries(getMaterialQueryKeyBuilder());
      }
    }
  );
};

export const useGetExistingMaterialQuery = (params: ExistingMaterialQuery) => {
  return useQuery<TableResult<ExistingMaterial>, ApiError>(
    getExistingMaterialKeyBuilder(params),
    () => materialClient.searchForExisting(params),
    {
      enabled: !!params.identificationNumber
    }
  );
};

export const usePriceChangeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, MaterialPriceOperation>({
    mutationFn: materialClient.changePrice,
    onSuccess: () => queryClient.invalidateQueries(getMaterialQueryKeyBuilder())
  });
};

export const useGetMaterialDetailQuery = (materialId?: string) => {
  return useQuery<Material, ApiError>({
    queryKey: getMaterialDetailQueryKeyBuilder(materialId || ''),
    queryFn: () => materialClient.getMaterialDetail(materialId || ''),
    enabled: !!materialId
  });
};
